import React, { useState, useEffect, useMemo } from "react";
import axios from "../axios";
import { toast } from "react-toastify";
import "./ProductTable.css";
import ProductDetailModal from "./ProductDetailModal";
import UXHint from "./UXHint";

/**
 * UnifiedProductTable
 * Vue unifiée vendeur : produits QR + produits barcode liés dans un seul tableau.
 *
 * Props :
 *   qrProducts  — Product[] (filtrés par ownerId, depuis Dashboard.js)
 *   user        — objet utilisateur connecté
 *   onSaveQR    — async (productId, { articleName, status }) => bool  — API + state + socket dans Dashboard.js
 *   onDeleteQR  — (id, name) => void — suppression QR (gérée par Dashboard.js)
 *   refreshKey  — incrémenter pour forcer le rechargement des barcode links
 */

const STOCK_LABELS = {
  "en stock":     "En stock",
  "promotion":    "Promotion",
  "indisponible": "Indisponible",
};

const STATUS_OPTIONS = [
  { value: "en stock",     label: "En stock" },
  { value: "promotion",    label: "Promotion" },
  { value: "indisponible", label: "Indisponible" },
];

// CSS class pour .status dans ProductTable.css
const statusCssClass = (status) => {
  if (status === "promotion")    return "promo";
  if (status === "indisponible") return "off";
  return "stock";
};

const ITEMS_PER_PAGE = 10;

const UnifiedProductTable = ({
  qrProducts = [],
  user,
  onSaveQR,
  onDeleteQR,
  refreshKey,
  onTotalChange,
}) => {
  // ── Barcode links (fetched internally) ─────────────────────────────────────
  const [barcodeLinks, setBarcodeLinks] = useState([]);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const vendorId   = user?.id || user?._id;
  const token      = user?.token;
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!vendorId) return;
    setLoadingLinks(true);
    axios
      .get(`/vendor-links/${vendorId}`, { headers: authHeader })
      .then((res) => setBarcodeLinks(res.data || []))
      .catch((err) =>
        console.error("❌ [UnifiedProductTable] Erreur chargement barcode links :", err)
      )
      .finally(() => setLoadingLinks(false));
  }, [vendorId, refreshKey]);

  // ── Scan counts (QR) ────────────────────────────────────────────────────────
  const [scanCounts, setScanCounts] = useState({});

  useEffect(() => {
    if (!qrProducts.length) return;
    const fetchCounts = async () => {
      try {
        const counts = {};
        for (const p of qrProducts) {
          if (!p.productId) continue;
          const res = await axios.get(`/scans/count/${p.productId}`);
          counts[p.productId] = res.data.count ?? 0;
        }
        setScanCounts(counts);
      } catch (err) {
        console.error("❌ [UnifiedProductTable] Erreur scanCounts QR :", err);
      }
    };
    fetchCounts();
  }, [qrProducts, refreshKey]);

  // ── Scan counts + lastScanAt (barcode) ──────────────────────────────────────
  const [barcodeScanCounts, setBarcodeScanCounts] = useState({});

  useEffect(() => {
    if (!barcodeLinks.length) return;
    const fetchCounts = async () => {
      try {
        const counts = {};
        for (const l of barcodeLinks) {
          const barcode = l.globalProductId?.barcode;
          if (!barcode || counts[barcode] !== undefined) continue;
          const res = await axios.get(`/scans/count/barcode/${barcode}`);
          counts[barcode] = { count: res.data.count ?? 0, lastScanAt: res.data.lastScanAt || null };
        }
        setBarcodeScanCounts(counts);
      } catch (err) {
        console.error("❌ [UnifiedProductTable] Erreur scanCounts barcode :", err);
      }
    };
    fetchCounts();
  }, [barcodeLinks]);

  // ── État d'édition inline (QR) ──────────────────────────────────────────────
  const [editingQRId, setEditingQRId]     = useState(null);
  const [editQRForm, setEditQRForm]       = useState({ articleName: "", status: "en stock" });
  const [savingQR, setSavingQR]           = useState(false);

  const startEditQR = (product) => {
    setEditingQRId(product._id);
    setEditQRForm({ articleName: product.articleName || "", status: product.status || "en stock" });
  };

  const cancelEditQR = () => {
    setEditingQRId(null);
    setEditQRForm({ articleName: "", status: "en stock" });
  };

  const saveQR = async (productId) => {
    setSavingQR(true);
    try {
      const ok = await onSaveQR(productId, editQRForm);
      if (ok !== false) setEditingQRId(null);
    } finally {
      setSavingQR(false);
    }
  };

  // ── État d'édition inline (barcode) ────────────────────────────────────────
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editLinkForm, setEditLinkForm]   = useState({ price: "", stock: "en stock", customNote: "" });
  const [savingLink, setSavingLink]       = useState(false);
  const [deletingId, setDeletingId]       = useState(null);

  // ── Fiche produit (modal détail) ────────────────────────────────────────────
  const [selectedItem, setSelectedItem] = useState(null);

  const startEditBarcode = (link) => {
    setEditingLinkId(link._id);
    setEditLinkForm({
      price:      link.price != null ? link.price : "",
      stock:      link.stock || "en stock",
      customNote: link.customNote || "",
    });
  };

  const cancelEditBarcode = () => {
    setEditingLinkId(null);
    setEditLinkForm({ price: "", stock: "en stock", customNote: "" });
  };

  const saveBarcode = async (linkId) => {
    setSavingLink(true);
    try {
      const updatedData = {
        price:      editLinkForm.price !== "" ? parseFloat(editLinkForm.price) : null,
        stock:      editLinkForm.stock,
        customNote: editLinkForm.customNote,
      };
      await axios.put(`/vendor-links/${linkId}`, updatedData, { headers: authHeader });
      setBarcodeLinks((prev) =>
        prev.map((l) => (l._id === linkId ? { ...l, ...updatedData } : l))
      );
      setEditingLinkId(null);
      toast.success("✅ Produit barcode mis à jour");
    } catch (err) {
      console.error("❌ [UnifiedProductTable] Erreur mise à jour lien :", err);
      toast.error("❌ Erreur lors de la mise à jour.");
    } finally {
      setSavingLink(false);
    }
  };

  const deleteBarcode = async (linkId, name) => {
    if (!window.confirm(`Dissocier le produit "${name}" de votre catalogue ?`)) return;
    setDeletingId(linkId);
    try {
      await axios.delete(`/vendor-links/${linkId}`, { headers: authHeader });
      setBarcodeLinks((prev) => prev.filter((l) => l._id !== linkId));
      toast.success("🔗 Produit dissocié");
    } catch (err) {
      console.error("❌ [UnifiedProductTable] Erreur suppression lien :", err);
      toast.error("❌ Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  // ── Fiche détail : helpers ─────────────────────────────────────────────────

  const getBarcodeData = (barcode) => barcodeScanCounts[barcode] || { count: 0, lastScanAt: null };

  const getScanCount = (item) => {
    if (item.type === "qr")      return scanCounts[item._raw.productId] ?? null;
    if (item.type === "barcode") return getBarcodeData(item._raw.globalProductId?.barcode).count ?? null;
    return null;
  };

  const getHeatBadge = (count) => {
    if (count >= 10) return { emoji: "🔥", label: "Hit du moment", color: "#dc2626", bg: "#fee2e2" };
    if (count >= 6)  return { emoji: "🟧", label: "Produit chaud",   color: "#ea580c", bg: "#ffedd5" };
    if (count >= 2)  return { emoji: "🟨", label: "Intérêt modéré",  color: "#ca8a04", bg: "#fef9c3" };
    return               { emoji: "🟦", label: "Très calme",       color: "#2563eb", bg: "#dbeafe" };
  };

  const timeAgoShort = (date) => {
    if (!date) return null;
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 3600)  return `il y a ${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
  };

  const handleModalSave = async (item, editData) => {
    if (item.type === "qr") {
      const ok = await onSaveQR(item._id, { articleName: editData.name, status: editData.status });
      if (ok !== false) setSelectedItem(null);
    } else {
      try {
        const updatedLink = {
          price:      editData.price !== "" ? parseFloat(editData.price) : null,
          stock:      editData.status,
          customNote: editData.customNote,
        };
        await axios.put(`/vendor-links/${item._id}`, updatedLink, { headers: authHeader });
        // Mettre à jour le nom dans GlobalProduct si modifié
        const globalId = item._raw.globalProductId?._id;
        if (editData.name !== item.name && globalId) {
          await axios.put(`/global-products/${globalId}`, { name: editData.name }, { headers: authHeader });
        }
        setBarcodeLinks((prev) =>
          prev.map((l) =>
            l._id !== item._id ? l : {
              ...l,
              ...updatedLink,
              globalProductId: { ...l.globalProductId, name: editData.name },
            }
          )
        );
        setSelectedItem(null);
        toast.success("✅ Produit mis à jour");
      } catch (err) {
        console.error("❌ [UnifiedProductTable] Erreur mise à jour depuis fiche :", err);
        toast.error("❌ Erreur lors de la mise à jour.");
        throw err; // remontée pour que le drawer reste ouvert
      }
    }
  };

  const handleModalDelete = (item) => {
    setSelectedItem(null);
    if (item.type === "qr")      onDeleteQR(item._id, item.name);
    else                         deleteBarcode(item._id, item.name);
  };

  // ── Normalisation + fusion ─────────────────────────────────────────────────
  const normalizedQR = useMemo(
    () =>
      qrProducts.map((p) => ({
        _id:        p._id,
        type:       "qr",
        name:       p.articleName || "—",
        identifier: p.productId   || "—",
        status:     p.status      || "en stock",
        createdAt:  p.createdAt   || null,
        _raw:       p,
      })),
    [qrProducts]
  );

  const normalizedBarcode = useMemo(
    () =>
      barcodeLinks.map((l) => ({
        _id:        l._id,
        type:       "barcode",
        name:       l.globalProductId?.name    || "—",
        identifier: l.globalProductId?.barcode || "—",
        status:     l.stock                    || "en stock",
        createdAt:  l.createdAt                || null,
        _raw:       l,
      })),
    [barcodeLinks]
  );

  const allItems = useMemo(() => {
    const merged = [...normalizedQR, ...normalizedBarcode];
    merged.sort((a, b) => {
      const ca = a.type === "barcode"
        ? (barcodeScanCounts[a._raw.globalProductId?.barcode]?.count ?? 0)
        : (scanCounts[a._raw.productId] ?? 0);
      const cb = b.type === "barcode"
        ? (barcodeScanCounts[b._raw.globalProductId?.barcode]?.count ?? 0)
        : (scanCounts[b._raw.productId] ?? 0);
      if (cb !== ca) return cb - ca;
      const da = a.createdAt ? new Date(a.createdAt) : 0;
      const db = b.createdAt ? new Date(b.createdAt) : 0;
      return db - da;
    });
    return merged;
  }, [normalizedQR, normalizedBarcode, barcodeScanCounts, scanCounts]);

  useEffect(() => {
    onTotalChange?.(allItems.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allItems.length]);

  // ── Filtres ────────────────────────────────────────────────────────────────
  const [search,       setSearch]       = useState("");
  const [typeFilter,   setTypeFilter]   = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage,  setCurrentPage]  = useState(1);
  const [isMobile,     setIsMobile]     = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const filtered = useMemo(() => {
    return allItems.filter((item) => {
      if (typeFilter   !== "all" && item.type   !== typeFilter)   return false;
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        if (!item.name.toLowerCase().includes(q) && !item.identifier.toLowerCase().includes(q))
          return false;
      }
      return true;
    });
  }, [allItems, typeFilter, statusFilter, search]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated  = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const changePage = (delta) =>
    setCurrentPage((p) => Math.min(Math.max(1, p + delta), totalPages));

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <div className="product-table-container" id="product-table">

      {/* En-tête */}
      <h2 className="title">📋 Mes produits</h2>

      {/* Micro-guidage UX */}
      <UXHint
        id="table_row_click"
        text={<><strong>Cliquez sur une ligne</strong> pour ouvrir la fiche complète. Le bouton <strong>Modifier</strong> lance une édition rapide directement dans le tableau.</>}
        style={{ margin: "0 var(--space-4) 14px" }}
      />

      {/* Filtres */}
      <div className="filters">
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          placeholder="🔍 Rechercher par nom ou identifiant..."
        />
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">Tous les types</option>
          <option value="qr">QR Code</option>
          <option value="barcode">Code-barres</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
        >
          <option value="all">Tous les statuts</option>
          <option value="en stock">En stock</option>
          <option value="promotion">Promotion</option>
          <option value="indisponible">Indisponible</option>
        </select>
      </div>

      {/* Résumé */}
      <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", padding: "0 var(--space-4)", marginBottom: "var(--space-2)" }}>
        {filtered.length} produit{filtered.length !== 1 ? "s" : ""}
        {" — "}
        {normalizedQR.length} QR · {normalizedBarcode.length} barcode
        {loadingLinks && " · chargement…"}
      </p>

      {/* Tableau */}
      {/* ── Vue cartes — mobile uniquement ── */}
      {isMobile && (
        <div>
          {paginated.length === 0 && (
            <p style={{ textAlign: "center", color: "#94a3b8", fontSize: "13px", padding: "24px 0" }}>Aucun produit trouvé</p>
          )}
          {paginated.map((item) => {
            const isEditingQR      = item.type === "qr"      && editingQRId   === item._id;
            const isEditingBarcode = item.type === "barcode" && editingLinkId === item._id;
            const isDeleting       = deletingId === item._id;
            const price            = item.type === "barcode" && item._raw?.price != null ? item._raw.price : null;

            const btnStyle = { flex: 1, fontSize: "12px", padding: "6px 10px", cursor: "pointer" };

            return (
              <div key={`m-${item.type}-${item._id}`} style={{ background: "white", borderRadius: "12px", padding: "12px", marginBottom: "8px", border: "1px solid #f1f5f9", boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
                {/* Nom + badge type + statut sur la même ligne */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", flexWrap: "wrap" }}>
                  {isEditingQR ? (
                    <input className="inline-input" value={editQRForm.articleName} onChange={(e) => setEditQRForm(prev => ({ ...prev, articleName: e.target.value }))} style={{ flex: 1, minWidth: 0 }} />
                  ) : (
                    <strong style={{ fontSize: "14px", color: "#0f172a" }}>{item.name}</strong>
                  )}
                  <span className={`type-badge type-badge--${item.type}`} style={{ flexShrink: 0 }}>{item.type === "qr" ? "QR" : "Barcode"}</span>
                  <div style={{ flexShrink: 0 }}>
                    {isEditingQR ? (
                      <select className="inline-input" value={editQRForm.status} onChange={(e) => setEditQRForm(prev => ({ ...prev, status: e.target.value }))}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : isEditingBarcode ? (
                      <select className="inline-input" value={editLinkForm.stock} onChange={(e) => setEditLinkForm(prev => ({ ...prev, stock: e.target.value }))}>
                        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <span className={`status ${statusCssClass(item.status)}`}>{STOCK_LABELS[item.status] || item.status}</span>
                    )}
                  </div>
                </div>

                {/* Prix barcode */}
                {!isEditingQR && !isEditingBarcode && price != null && (
                  <div style={{ fontSize: "12px", color: "#475569", marginBottom: "4px" }}>💶 {price} €</div>
                )}

                {/* Champs inline barcode en édition */}
                {isEditingBarcode && (
                  <div style={{ display: "flex", gap: "6px", marginBottom: "8px", flexWrap: "wrap" }}>
                    <input type="number" min="0" step="0.01" value={editLinkForm.price} onChange={(e) => setEditLinkForm(prev => ({ ...prev, price: e.target.value }))} placeholder="Prix €" className="inline-input" style={{ width: "80px" }} />
                    <input type="text" value={editLinkForm.customNote} onChange={(e) => setEditLinkForm(prev => ({ ...prev, customNote: e.target.value }))} placeholder="Note" className="inline-input" style={{ flex: 1, minWidth: "80px" }} />
                  </div>
                )}

                {/* Boutons d'action — 50/50 sur la même ligne */}
                <div style={{ display: "flex", gap: "6px", marginTop: "8px" }}>
                  {isEditingQR ? (
                    <>
                      <button className="btn btn-primary btn-sm" style={btnStyle} onClick={() => saveQR(item._id)} disabled={savingQR}>{savingQR ? "⏳" : "💾 Sauvegarder"}</button>
                      <button className="btn btn-secondary btn-sm" style={btnStyle} onClick={cancelEditQR} disabled={savingQR}>✕ Annuler</button>
                    </>
                  ) : isEditingBarcode ? (
                    <>
                      <button className="btn btn-primary btn-sm" style={btnStyle} onClick={() => saveBarcode(item._id)} disabled={savingLink}>{savingLink ? "⏳" : "💾 Sauvegarder"}</button>
                      <button className="btn btn-secondary btn-sm" style={btnStyle} onClick={cancelEditBarcode} disabled={savingLink}>✕ Annuler</button>
                    </>
                  ) : item.type === "qr" ? (
                    <>
                      <button className="btn btn-secondary btn-sm" style={btnStyle} onClick={() => startEditQR(item._raw)}>✏️ Modifier</button>
                      <button className="btn btn-danger btn-sm" style={btnStyle} onClick={() => onDeleteQR(item._id, item.name)}>🗑️ Supprimer</button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-secondary btn-sm" style={btnStyle} onClick={() => startEditBarcode(item._raw)}>✏️ Modifier</button>
                      <button className="btn btn-danger btn-sm" style={btnStyle} onClick={() => deleteBarcode(item._id, item.name)} disabled={isDeleting}>{isDeleting ? "⏳" : "🔗 Dissocier"}</button>
                    </>
                  )}
                </div>
                {!isEditingQR && !isEditingBarcode && (
                  <button
                    onClick={() => setSelectedItem(item)}
                    style={{ width: "100%", marginTop: "8px", padding: "8px", background: "#F1F5F9", color: "#475569", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: "pointer", textAlign: "center" }}
                  >
                    📋 Voir la fiche complète
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Vue table — desktop uniquement ── */}
      {!isMobile && <div className="table-wrapper">
        <table className="table product-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Nom</th>
              <th>Identifiant</th>
              <th>Statut</th>
              <th>Scans</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-results">Aucun produit trouvé</td>
              </tr>
            ) : (
              paginated.map((item, idx) => {
                const isEditingQR      = item.type === "qr"      && editingQRId   === item._id;
                const isEditingBarcode = item.type === "barcode" && editingLinkId === item._id;
                const isDeleting       = deletingId === item._id;
                const barcode          = item.type === "barcode" ? item._raw.globalProductId?.barcode : null;
                const bData            = barcode ? getBarcodeData(barcode) : null;
                const crushCount       = bData?.count ?? (item.type === "qr" ? (scanCounts[item._raw.productId] ?? 0) : 0);
                const heat             = item.type === "barcode" ? getHeatBadge(crushCount) : null;
                const lastScanLabel    = bData?.lastScanAt ? timeAgoShort(bData.lastScanAt) : (bData ? "jamais scanné" : null);
                const isTopProduct     = idx === 0 && crushCount > 0;

                return (
                  <tr
                    key={`${item.type}-${item._id}`}
                    style={{ cursor: (!isEditingQR && !isEditingBarcode) ? "pointer" : "default" }}
                    onClick={() => { if (!isEditingQR && !isEditingBarcode) setSelectedItem(item); }}
                  >

                    {/* Type */}
                    <td>
                      <span className={`type-badge type-badge--${item.type}`}>
                        {item.type === "qr" ? "QR" : "Barcode"}
                      </span>
                    </td>

                    {/* Nom — éditable inline pour QR */}
                    <td className="name">
                      {isEditingQR ? (
                        <input
                          className="inline-input"
                          value={editQRForm.articleName}
                          onChange={(e) =>
                            setEditQRForm((prev) => ({ ...prev, articleName: e.target.value }))
                          }
                        />
                      ) : (
                        <>
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                            <strong>{item.name}</strong>
                            {isTopProduct && (
                              <span style={{ fontSize: "10px", fontWeight: 700, color: "#D41A6E", background: "#fdf2f8", borderRadius: "99px", padding: "1px 7px", border: "1px solid #fbcfe8" }}>
                                🔝 Le plus populaire
                              </span>
                            )}
                            {heat && (
                              <span style={{ fontSize: "10px", fontWeight: 700, color: heat.color, background: heat.bg, borderRadius: "99px", padding: "1px 7px" }}>
                                {heat.emoji} {heat.label}
                              </span>
                            )}
                          </div>
                          {lastScanLabel && (
                            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                              Dernier scan : {lastScanLabel}
                            </div>
                          )}
                        </>
                      )}
                    </td>

                    {/* Identifiant */}
                    <td className="id">
                      <span style={{ fontFamily: "monospace", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                        {item.identifier}
                      </span>
                    </td>

                    {/* Statut — select inline pour les deux types en édition */}
                    <td>
                      {isEditingQR ? (
                        <select
                          className="inline-input"
                          value={editQRForm.status}
                          onChange={(e) =>
                            setEditQRForm((prev) => ({ ...prev, status: e.target.value }))
                          }
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : isEditingBarcode ? (
                        <select
                          className="inline-input"
                          value={editLinkForm.stock}
                          onChange={(e) =>
                            setEditLinkForm((prev) => ({ ...prev, stock: e.target.value }))
                          }
                        >
                          {STATUS_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className={`status ${statusCssClass(item.status)}`}>
                          {STOCK_LABELS[item.status] || item.status}
                        </span>
                      )}
                    </td>

                    {/* Scans */}
                    <td className="scans" style={{ textAlign: "center" }}>
                      {item.type === "qr"
                        ? (scanCounts[item._raw.productId] ?? "…")
                        : (bData ? bData.count : "…")}
                    </td>

                    {/* Date */}
                    <td className="date">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleDateString()
                        : "—"}
                    </td>

                    {/* Actions — stopPropagation pour ne pas déclencher l'ouverture du modal */}
                    <td className="action" onClick={(e) => e.stopPropagation()}>
                      {isEditingQR ? (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => saveQR(item._id)}
                            disabled={savingQR}
                          >
                            {savingQR ? "⏳" : "💾 Sauvegarder"}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEditQR}
                            disabled={savingQR}
                          >
                            ✕ Annuler
                          </button>
                        </div>
                      ) : isEditingBarcode ? (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={editLinkForm.price}
                            onChange={(e) =>
                              setEditLinkForm((prev) => ({ ...prev, price: e.target.value }))
                            }
                            placeholder="Prix €"
                            className="inline-input"
                            style={{ width: "80px" }}
                          />
                          <input
                            type="text"
                            value={editLinkForm.customNote}
                            onChange={(e) =>
                              setEditLinkForm((prev) => ({ ...prev, customNote: e.target.value }))
                            }
                            placeholder="Note"
                            className="inline-input"
                            style={{ width: "100px" }}
                          />
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => saveBarcode(item._id)}
                            disabled={savingLink}
                          >
                            {savingLink ? "⏳" : "💾"}
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={cancelEditBarcode}
                            disabled={savingLink}
                          >
                            ✕
                          </button>
                        </div>
                      ) : item.type === "qr" ? (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => startEditQR(item._raw)}
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => onDeleteQR(item._id, item.name)}
                          >
                            🗑️ Supprimer
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => startEditBarcode(item._raw)}
                          >
                            ✏️ Modifier
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteBarcode(item._id, item.name)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "⏳" : "🔗 Dissocier"}
                          </button>
                        </div>
                      )}
                    </td>

                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination" style={{ display: "flex", gap: "8px", justifyContent: "center", paddingTop: "var(--space-2)" }}>
          <button
            className="btn btn-secondary btn-sm"
            disabled={currentPage === 1}
            onClick={() => changePage(-1)}
          >
            ◀
          </button>
          <span style={{ padding: "6px 12px", fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            {currentPage} / {totalPages}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            disabled={currentPage === totalPages}
            onClick={() => changePage(1)}
          >
            ▶
          </button>
        </div>
      )}

      {/* Fiche produit détail */}
      <ProductDetailModal
        item={selectedItem}
        scanCount={selectedItem ? getScanCount(selectedItem) : null}
        onClose={() => setSelectedItem(null)}
        onSave={handleModalSave}
        onDelete={() => handleModalDelete(selectedItem)}
      />

    </div>
  );
};

export default UnifiedProductTable;
